import { useCallback, useEffect, useRef } from 'react';
import {
  buildSuggestionFeedbackBatch,
  type SuggestionFeedbackBucket,
  type SuggestionHunkDecision,
} from '../../../../../util/zoriEditingExtensions';
import type { SendAgentFeedbackInput } from '../../../../../hooks/useSendAgentFeedback';

type SessionAccumulator = {
  traceId: string;
  accepted: SuggestionFeedbackBucket;
  rejected: SuggestionFeedbackBucket;
};

export type RecordSuggestionHunkDecision = (args: {
  /** Suggestion trace id from the active impression; blank ids are ignored. */
  traceId: string | undefined;
  decision: SuggestionHunkDecision;
  /** Optional per-hunk preview. Bulk actions omit this and pass `count`. */
  detail?: string;
  /** Number of hunks resolved by this action (default 1; bulk passes N). */
  count?: number;
}) => void;

export type SuggestionFeedbackBatchApi = {
  recordSuggestionHunkDecision: RecordSuggestionHunkDecision;
  flushSuggestionFeedback: () => void;
};

const emptyBucket = (): SuggestionFeedbackBucket => ({ count: 0, details: [] });

/**
 * Batches per-hunk Zori suggestion review decisions so a review session emits at
 * most two `POST /v1/agents-v2/feedback` calls (one accepted, one rejected) per
 * suggestion trace instead of one per hunk click. This keeps the agents
 * `#feed-ai-agents` Slack channel quiet (Option B) while preserving the distinct
 * accepted/rejected Langfuse score names.
 *
 * Decisions accumulate in a ref keyed by trace id. The batch is flushed when:
 *   - the whole diff is resolved (caller invokes `flushSuggestionFeedback` from
 *     the editor's `onAllDiffsResolved` handler), or
 *   - the editor unmounts mid-review (navigation away) — handled here, or
 *   - a decision arrives for a different trace id (a re-run produced a new diff).
 */
export function useSuggestionFeedbackBatch(
  sendAgentFeedback: (input: SendAgentFeedbackInput) => Promise<unknown>,
): SuggestionFeedbackBatchApi {
  const accumulatorRef = useRef<SessionAccumulator | null>(null);
  // Keep the latest sender without re-subscribing the unmount-flush effect.
  const sendRef = useRef(sendAgentFeedback);
  sendRef.current = sendAgentFeedback;

  const flushSuggestionFeedback = useCallback(() => {
    const session = accumulatorRef.current;
    accumulatorRef.current = null;
    if (!session) {
      return;
    }
    const payloads = buildSuggestionFeedbackBatch(session.traceId, {
      accepted: session.accepted,
      rejected: session.rejected,
    });
    for (const payload of payloads) {
      void Promise.resolve(sendRef.current(payload)).catch(() => {
        /* telemetry only — never block the review UX */
      });
    }
  }, []);

  const recordSuggestionHunkDecision =
    useCallback<RecordSuggestionHunkDecision>(
      ({ traceId, decision, detail, count = 1 }) => {
        const trimmedTraceId = traceId?.trim();
        if (!trimmedTraceId || count <= 0) {
          return;
        }
        // A decision for a different trace means a new suggestion session — flush
        // the previous one before starting to accumulate the new one.
        if (
          accumulatorRef.current &&
          accumulatorRef.current.traceId !== trimmedTraceId
        ) {
          flushSuggestionFeedback();
        }
        if (!accumulatorRef.current) {
          accumulatorRef.current = {
            traceId: trimmedTraceId,
            accepted: emptyBucket(),
            rejected: emptyBucket(),
          };
        }
        const bucket =
          decision === 'accepted'
            ? accumulatorRef.current.accepted
            : accumulatorRef.current.rejected;
        bucket.count += count;
        const trimmedDetail = detail?.trim();
        if (trimmedDetail) {
          bucket.details.push(trimmedDetail);
        }
      },
      [flushSuggestionFeedback],
    );

  useEffect(() => () => flushSuggestionFeedback(), [flushSuggestionFeedback]);

  return { recordSuggestionHunkDecision, flushSuggestionFeedback };
}
